import { useCallback } from "react";

import { useQueryStateContext } from "@/common/hooks/useQueryStateContext";

type QueryStateBuilderOptions = {
  history?: "push" | "replace";
  shallow?: boolean;
  scroll?: boolean;
};

type InternalQueryStateBuilderOptions = {
  history: "push" | "replace";
  shallow: boolean;
  scroll: boolean;
};

type QueryStateParser<T> = {
  parse: (query: string | null, defaultValue: T) => T;
  serialize: (value: T, defaultValue: T) => string | null;
};

export class QueryStateBuilder<T> {
  options: InternalQueryStateBuilderOptions;
  defaultValue: T;
  parser: QueryStateParser<T>;

  private defaultOptions = {
    history: "push" as const,
    shallow: true,
    scroll: false,
  };

  constructor(parser: QueryStateParser<T>, defaultValue: T, options: QueryStateBuilderOptions = {}) {
    this.options = { ...this.defaultOptions, ...options };
    this.defaultValue = defaultValue;
    this.parser = parser;
  }

  withDefault(defaultValue: T) {
    return new QueryStateBuilder(this.parser, defaultValue, this.options);
  }

  withOptions(options: QueryStateBuilderOptions) {
    return new QueryStateBuilder(this.parser, this.defaultValue, { ...this.options, ...options });
  }
}

const regexParser = (regex: RegExp): QueryStateParser<string> => ({
  parse: (value: string | null, defaultValue: string) => {
    if (!value) {
      return defaultValue;
    }

    const isMatch = value.match(regex);

    return isMatch ? value : defaultValue;
  },
  serialize: (value: string, defaultValue: string) => {
    if (!value || value === defaultValue) {
      return null;
    }

    const isMatch = value.match(regex);

    return isMatch ? value : null;
  },
});

const hexColorRegex = /^#?([0-9a-fA-F]{6})$/;
const hexColorParser: QueryStateParser<string> = {
  parse: (value: string | null, defaultValue: string) => {
    const parsedValue = regexParser(hexColorRegex).parse(value, defaultValue);

    if (parsedValue === defaultValue) {
      return defaultValue;
    }

    if (parsedValue.startsWith("#")) {
      return parsedValue;
    }

    return `#${parsedValue}`;
  },
  serialize: regexParser(hexColorRegex).serialize,
};

const stringParser: QueryStateParser<string> = {
  parse: (value: string | null, defaultValue: string) => {
    if (!value) {
      return defaultValue;
    }

    return value;
  },
  serialize: (value: string, defaultValue: string) => {
    if (!value || value === defaultValue) {
      return null;
    }

    return value;
  },
};

const numberParser: QueryStateParser<number> = {
  parse: (value: string | null, defaultValue: number) => {
    if (value === null) {
      return defaultValue;
    }
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  },
  serialize: (value: number, defaultValue: number) => {
    if (value === null || value === defaultValue) {
      return null;
    }
    return value.toString();
  },
};

const booleanParser: QueryStateParser<boolean> = {
  parse: (value: string | null, defaultValue: boolean) => {
    if (!value) {
      return defaultValue;
    }
    return value.toLowerCase() === "true" ? true : value.toLowerCase() === "false" ? false : defaultValue;
  },
  serialize: (value: boolean, defaultValue: boolean) => {
    if (value === null || value === defaultValue) {
      return null;
    }
    return value.toString();
  },
};

const stringLiteralParser = <T extends readonly string[]>(literals: T): QueryStateParser<T[number] | null> => ({
  parse: (value: string | null, defaultValue: T[number] | null) => {
    if (!value) {
      return defaultValue;
    }
    return literals.includes(value as T[number]) ? (value as T[number]) : defaultValue;
  },
  serialize: (value: T[number] | null, defaultValue: T[number] | null) => {
    if (!value || value === defaultValue) {
      return null;
    }
    return literals.includes(value) ? value : null;
  },
});

const stringArrayParser: QueryStateParser<string[]> = {
  parse: (value: string | null, defaultValue: string[]) => {
    if (!value) {
      return defaultValue;
    }

    // Split by comma and trim whitespace from each item
    const items = value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    return items.length > 0 ? items : defaultValue;
  },
  serialize: (value: string[], defaultValue: string[]) => {
    if (!value || value.length === 0 || (defaultValue.length === 0 && value.length === 0)) {
      return null;
    }

    return value.join(",");
  },
};

export const parseAsHexColor = new QueryStateBuilder(hexColorParser, "#FFFFFF");
export const parseAsString = new QueryStateBuilder(stringParser, "");
export const parseAsStringArray = new QueryStateBuilder(stringArrayParser, []);
export const parseAsNumber = new QueryStateBuilder(numberParser, 0);
export const parseAsBoolean = new QueryStateBuilder(booleanParser, false);
export const parseAsStringLiteral = <T extends readonly string[]>(literals: T) => {
  return new QueryStateBuilder(stringLiteralParser(literals), null);
};
export const parseAsEnum = <T extends Record<string, string>>(enumObj: T) => {
  return new QueryStateBuilder(enumParser(enumObj), Object.values(enumObj)[0] as T[keyof T]);
};

const enumParser = <T extends Record<string, string>>(enumObj: T): QueryStateParser<T[keyof T]> => ({
  parse: (value: string | null, defaultValue: T[keyof T]) => {
    if (!value) {
      return defaultValue;
    }

    const enumValues = Object.values(enumObj);
    return enumValues.includes(value as T[keyof T]) ? (value as T[keyof T]) : defaultValue;
  },
  serialize: (value: T[keyof T], defaultValue: T[keyof T]) => {
    if (!value || value === defaultValue) {
      return null;
    }

    const enumValues = Object.values(enumObj);
    return enumValues.includes(value) ? value : null;
  },
});

/**
 * A hook for managing URL query parameters with type-safe parsing and serialization.
 *
 * @template T The type of the query parameter value
 * @param {string} key The query parameter key
 * @param {QueryStateBuilder<T>} builder A builder can be created using the `parseAsString`, `parseAsNumber`, `parseAsBoolean`, and `parseAsStringLiteral` functions
 * @returns {[T, (value: T | null) => void]} A tuple containing the current value and a setter function
 *
 * @example
 * // Basic string parameter
 * const [name, setName] = useQueryState('name', parseAsString);
 *
 * @example
 * // Number parameter with default value
 * const [count, setCount] = useQueryState('count', parseAsNumber.withDefault(10));
 *
 * @example
 * // Boolean parameter with custom router options
 * const [isOpen, setIsOpen] = useQueryState(
 *   'open',
 *   parseAsBoolean.withOptions({ history: 'replace' })
 * );
 *
 * @example
 * // String literal parameter with type safety
 * const [sort, setSort] = useQueryState(
 *   'sort',
 *   parseAsStringLiteral(['asc', 'desc'] as const)
 * );
 *
 * @example
 * // Hex color parameter with type safety
 * const [color, setColor] = useQueryState('color', parseAsHexColor);
 */
export const useQueryState = <T>(key: string, builder: QueryStateBuilder<T>) => {
  const { getSearchParams, enqueueRouterOperation } = useQueryStateContext();
  const {
    parser,
    defaultValue,
    options: { history, shallow, scroll },
  } = builder;

  const searchParams = getSearchParams();

  const value = parser.parse(searchParams.get(key), defaultValue);

  const setValue = useCallback(
    (value: T | null) => {
      const newValue = parser.serialize(value ?? defaultValue, defaultValue);

      enqueueRouterOperation({
        key,
        value: newValue,
        history,
        shallow,
        scroll,
      });
    },
    [defaultValue, enqueueRouterOperation, history, key, parser, scroll, shallow]
  );

  return [value, setValue] as [T, (value: T | null) => void];
};

export const parseSSRQueryState = <T>(value: string | string[] | undefined, builder: QueryStateBuilder<T>) => {
  if (value === undefined || Array.isArray(value)) {
    return builder.parser.parse(null, builder.defaultValue);
  }

  return builder.parser.parse(value, builder.defaultValue);
};
